/* import __COLOCATED_TEMPLATE__ from './issue-gift-card-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import ajax from 'embercom/lib/ajax';
import type IntlService from 'ember-intl/services/intl';
import type Session from 'embercom/services/session';
import { captureException } from 'embercom/lib/sentry';
import type {
  ShopifyOrder,
  ShopifyLineItem,
} from 'embercom/components/inbox2/conversation-details-sidebar/sidebar-sections/shopify';
import {
  buildPerItemNote,
  hasAllItemReasons,
  type LineItemReason,
} from 'embercom/lib/inbox/shopify/compensation-reasons';

interface SelectedLineItem {
  lineItemId: number;
  quantity?: number;
  selected?: boolean;
}

interface IssueGiftCardModalArgs {
  isOpen: boolean;
  onModalClose: () => void;
  order: ShopifyOrder;
  shopifyDomain: string | null;
  onGiftCardSuccess?: () => void;
  conversationId?: number;
}

export default class IssueGiftCardModal extends Component<IssueGiftCardModalArgs> {
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare session: Session;
  @service declare intl: IntlService;

  @tracked giftCardValue = '';
  @tracked note = '';
  @tracked expiryDate = '';
  @tracked notifyCustomer = true;
  @tracked selectedLineItems: SelectedLineItem[] = [];
  @tracked isSuccessState = false;
  @tracked giftCardId: string | null = null;
  @tracked giftCardCode: string | null = null;
  @tracked lineItemReasons: LineItemReason[] = [];
  @tracked isSelectAllChecked = false;
  @tracked genericRefundReason = '';
  @tracked workspaceRefundReasons: string[] = [];
  @tracked workspaceRefundReasonsFetchError: string | null = null;

  get isPerItemRefundReasonEnabled() {
    return this.session.workspace.isFeatureEnabled('tdf-shopify-per-item-refund-reason');
  }

  get workspaceReasonOptions() {
    return this.workspaceRefundReasons.map((reason) => ({
      value: reason,
      label: reason,
    }));
  }

  get compensationNote(): string {
    if (this.isSelectAllChecked && this.genericRefundReason) {
      return this.genericRefundReason;
    }
    if (this.isPerItemRefundReasonEnabled) {
      return buildPerItemNote(
        this.selectedLineItems
          .filter((item) => (item.quantity ?? 0) > 0)
          .map((item) => ({
            lineItemId: item.lineItemId,
            quantity: item.quantity ?? 0,
          })),
        this.lineItemReasons,
        this.args.order.line_items,
      );
    }
    return this.note;
  }

  get displayGiftCardId(): string {
    if (!this.giftCardId) {
      return '';
    }
    let parts = this.giftCardId.split('/');
    return parts[parts.length - 1] ?? this.giftCardId;
  }

  get formattedGiftCardValue(): string {
    return `${this.currencySymbol}${this.parsedGiftCardValue.toFixed(2)}`;
  }

  get formattedExpiryDate(): string {
    if (!this.expiryDate) {
      return '';
    }
    let [year, month, day] = this.expiryDate.split('-');
    return `${day}/${month}/${year}`;
  }

  constructor(owner: unknown, args: IssueGiftCardModalArgs) {
    super(owner, args);
    this.resetModalData();

    if (this.isPerItemRefundReasonEnabled) {
      void taskFor(this.fetchWorkspaceRefundReasons).perform();
    }
  }

  get currencySymbol() {
    return this.args.order.total?.charAt(0) ?? '$';
  }

  private formatDate(date: Date): string {
    let year = date.getFullYear();
    let month = String(date.getMonth() + 1).padStart(2, '0');
    let day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get todayDate() {
    return this.formatDate(new Date());
  }

  get oneYearFromToday() {
    let date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return this.formatDate(date);
  }

  get orderTotal() {
    let totalString = this.args.order.total?.substring(1) ?? '0.00';
    return parseFloat(totalString.replace(/[^0-9.]/g, ''));
  }

  get maxGiftCardValue() {
    return this.orderTotal * 1.5;
  }

  get formattedMaxValue() {
    return this.currencySymbol + this.maxGiftCardValue.toFixed(2);
  }

  get parsedGiftCardValue() {
    return parseFloat(this.giftCardValue) || 0;
  }

  get hasValidGiftCardValue() {
    return this.parsedGiftCardValue > 0 && this.parsedGiftCardValue <= this.maxGiftCardValue;
  }

  get hasSelectedItems() {
    return this.selectedLineItems.some(
      (item) => item.selected === true || (item.quantity ?? 0) > 0,
    );
  }

  get selectedLineItemsTotal() {
    return this.selectedLineItems.reduce((total, selectedItem) => {
      if (selectedItem.selected === true || (selectedItem.quantity ?? 0) > 0) {
        let lineItem = this.args.order.line_items.find(
          (item) => item.id === selectedItem.lineItemId,
        );
        if (lineItem) {
          let price = parseFloat(lineItem.price.replace(/[^0-9.]/g, ''));
          return total + price * (selectedItem.quantity ?? 0);
        }
      }
      return total;
    }, 0);
  }

  get hasAllRequiredReasons(): boolean {
    if (this.isSelectAllChecked) {
      return Boolean(this.genericRefundReason);
    }
    if (!this.isPerItemRefundReasonEnabled) {
      return Boolean(this.note.trim());
    }
    let activeItems = this.selectedLineItems
      .filter((item) => (item.quantity ?? 0) > 0)
      .map((item) => ({ lineItemId: item.lineItemId, quantity: item.quantity ?? 0 }));
    return hasAllItemReasons(activeItems, this.lineItemReasons);
  }

  get canSubmit() {
    if (this.isPerItemRefundReasonEnabled) {
      return Boolean(
        this.hasValidGiftCardValue &&
          this.hasSelectedItems &&
          this.hasAllRequiredReasons &&
          !this.isIssueGiftCardTaskRunning,
      );
    }
    return Boolean(
      this.hasValidGiftCardValue &&
        this.note.trim() &&
        this.hasSelectedItems &&
        !this.isIssueGiftCardTaskRunning,
    );
  }

  get isIssueGiftCardTaskRunning() {
    return taskFor(this.issueGiftCardTask).isRunning;
  }

  get isFetchingWorkspaceRefundReasons(): boolean {
    return taskFor(this.fetchWorkspaceRefundReasons).isRunning;
  }

  get hasWorkspaceRefundReasonsError(): boolean {
    return Boolean(this.workspaceRefundReasonsFetchError);
  }

  get shouldShowEmptyReasonsGuidance(): boolean {
    return (
      !this.isFetchingWorkspaceRefundReasons &&
      !this.hasWorkspaceRefundReasonsError &&
      this.workspaceRefundReasons.length === 0 &&
      this.isPerItemRefundReasonEnabled &&
      !this.isSelectAllChecked
    );
  }

  get isLineItemCompensatable() {
    return (lineItem: ShopifyLineItem): boolean => {
      let alreadyRefunded = lineItem.already_refunded ?? 0;
      let quantityCompensatable =
        lineItem.quantity_refundable ?? lineItem.quantity - alreadyRefunded;
      return quantityCompensatable > 0;
    };
  }

  get getLineItemReason() {
    void this.lineItemReasons.length;

    return (lineItemId: number): string => {
      return this.lineItemReasons.find((r) => r.lineItemId === lineItemId)?.reason ?? '';
    };
  }

  @action
  isLineItemSelected(lineItemId: number): boolean {
    let selectedItem = this.selectedLineItems.find((item) => item.lineItemId === lineItemId);
    return selectedItem
      ? selectedItem.selected === true || (selectedItem.quantity ?? 0) > 0
      : false;
  }

  @action
  getSelectedQuantity(lineItemId: number): number {
    let selectedItem = this.selectedLineItems.find((item) => item.lineItemId === lineItemId);
    return selectedItem?.quantity ?? 0;
  }

  @action
  toggleLineItemSelection(lineItemId: number) {
    let isCurrentlySelected = this.isLineItemSelected(lineItemId);
    let lineItem = this.args.order.line_items.find((item) => item.id === lineItemId);

    if (!lineItem) {
      return;
    }

    let targetQuantity = isCurrentlySelected
      ? 0
      : (lineItem.quantity_refundable ?? lineItem.quantity);
    this.updateLineItemQuantityValue(lineItemId, targetQuantity);
  }

  @action
  updateLineItemQuantity(lineItemId: number, event: Event) {
    let target = event.target as HTMLInputElement;
    let quantity = parseInt(target.value, 10);
    this.updateLineItemQuantityValue(lineItemId, quantity);
  }

  private updateLineItemQuantityValue(lineItemId: number, quantity: number) {
    let existingItemIndex = this.selectedLineItems.findIndex(
      (item) => item.lineItemId === lineItemId,
    );

    let normalizedQuantity = Math.max(0, quantity);

    if (existingItemIndex !== -1) {
      this.selectedLineItems[existingItemIndex] = {
        ...this.selectedLineItems[existingItemIndex],
        quantity: normalizedQuantity,
        selected: normalizedQuantity > 0,
      };
    } else {
      this.selectedLineItems = [
        ...this.selectedLineItems,
        {
          lineItemId,
          quantity: normalizedQuantity,
          selected: normalizedQuantity > 0,
        },
      ];
    }

    this.selectedLineItems = [...this.selectedLineItems];
    this.isSelectAllChecked = false;
    this.updateGiftCardValueFromSelection();
  }

  private updateGiftCardValueFromSelection() {
    let total = this.selectedLineItemsTotal;
    this.giftCardValue = total > 0 ? total.toFixed(2) : '';
  }

  @action
  updateLineItemReason(lineItemId: number, event: Event) {
    let target = event.target as HTMLSelectElement;
    let reason = target.value;
    let existingIndex = this.lineItemReasons.findIndex((r) => r.lineItemId === lineItemId);

    if (existingIndex >= 0) {
      this.lineItemReasons = this.lineItemReasons.map((r) =>
        r.lineItemId === lineItemId ? { ...r, reason } : r,
      );
    } else {
      this.lineItemReasons = [...this.lineItemReasons, { lineItemId, reason }];
    }
  }

  @action
  onGiftCardValueChange(event: Event) {
    let target = event.target as HTMLInputElement;
    let value = target.value;

    // Limit to 2 decimal places
    if (value.includes('.')) {
      let [integer, decimal] = value.split('.');
      if (decimal && decimal.length > 2) {
        value = `${integer}.${decimal.substring(0, 2)}`;
        target.value = value;
      }
    }

    this.giftCardValue = value;
  }

  @action
  onExpiryDateChange(event: Event) {
    let target = event.target as HTMLInputElement;
    this.expiryDate = target.value;
  }

  @action
  onNotifyCustomerChange(checked: boolean) {
    this.notifyCustomer = checked;
  }

  @action
  retryFetchWorkspaceRefundReasons() {
    void taskFor(this.fetchWorkspaceRefundReasons).perform();
  }

  @action
  toggleSelectAll() {
    this.isSelectAllChecked = !this.isSelectAllChecked;
    if (this.isSelectAllChecked) {
      if (
        this.workspaceRefundReasons.length === 0 &&
        !taskFor(this.fetchWorkspaceRefundReasons).isRunning
      ) {
        void taskFor(this.fetchWorkspaceRefundReasons).perform();
      }
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id && this.isLineItemCompensatable(item))
        .map((item) => ({
          lineItemId: item.id,
          quantity: item.quantity_refundable ?? item.quantity,
          selected: true,
        }));
      this.lineItemReasons = [];
      this.updateGiftCardValueFromSelection();
    } else {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({ lineItemId: item.id, quantity: 0, selected: false }));
      this.updateGiftCardValueFromSelection();
    }
  }

  @action
  updateGenericRefundReason(event: Event) {
    let target = event.target as HTMLSelectElement;
    this.genericRefundReason = target.value;
  }

  @action
  resetModalData() {
    this.giftCardValue = '';
    this.note = '';
    this.expiryDate = this.oneYearFromToday;
    this.notifyCustomer = true;
    this.isSuccessState = false;
    this.giftCardId = null;
    this.giftCardCode = null;
    this.lineItemReasons = [];
    this.isSelectAllChecked = false;
    this.genericRefundReason = '';
    this.selectedLineItems = this.args.order.line_items
      .filter((item) => item.id)
      .map((item) => ({
        lineItemId: item.id,
        quantity: 0,
        selected: false,
      }));
  }

  @action
  handleModalClose() {
    if (this.isSuccessState) {
      this.args.onGiftCardSuccess?.();
    }
    this.resetModalData();
    this.args.onModalClose();
  }

  @restartableTask
  *fetchWorkspaceRefundReasons(): TaskGenerator<void> {
    this.workspaceRefundReasonsFetchError = null;

    try {
      let response: any = yield ajax({
        type: 'GET',
        url: `/ember/shopify/integrations/workspace_refund_reasons?app_id=${this.session.workspace.id}`,
      });

      if (response && response.reasons) {
        this.workspaceRefundReasons = response.reasons;
      }
    } catch (error: unknown) {
      captureException(error);
      this.workspaceRefundReasonsFetchError = this.intl.t(
        'inbox.conversation-details-sidebar.shopify.refund-order-modal.refund-reasons-fetch-error',
      );
    }
  }

  @restartableTask
  *issueGiftCardTask(): TaskGenerator<void> {
    try {
      let selectedLineItemQuantities = this.selectedLineItems
        .filter((item) => item.selected === true || (item.quantity ?? 0) > 0)
        .map((item) => {
          let quantity = item.quantity ?? 0;
          if (item.selected === true && quantity === 0) {
            let lineItem = this.args.order.line_items.find((li) => li.id === item.lineItemId);
            quantity = lineItem?.quantity_refundable ?? lineItem?.quantity ?? 0;
          }
          return {
            line_item_id: item.lineItemId,
            quantity,
          };
        });

      if (selectedLineItemQuantities.length === 0) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.gift-card-modal.errors.no-items-selected',
          ),
        );
        return;
      }

      if (!this.compensationNote.trim()) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.gift-card-modal.errors.no-reason',
          ),
        );
        return;
      }

      if (!this.hasValidGiftCardValue) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.gift-card-modal.errors.invalid-value',
            { maxValue: this.formattedMaxValue },
          ),
        );
        return;
      }

      if (!this.expiryDate) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.gift-card-modal.errors.expiry-required',
          ),
        );
        return;
      }

      let lineItemsDetail = selectedLineItemQuantities.map((selected) => {
        let lineItem = this.args.order.line_items.find((li) => li.id === selected.line_item_id);
        return {
          title: lineItem?.title,
          quantity: selected.quantity,
          price: lineItem?.price,
        };
      });

      let params: any = {
        app_id: this.session.workspace.id,
        order_id: this.args.order.id,
        order_name: this.args.order.name,
        conversation_id: this.args.conversationId,
        gift_card_value: this.parsedGiftCardValue,
        reason: this.compensationNote,
        line_item_quantities: selectedLineItemQuantities,
        line_items_detail: lineItemsDetail,
        notify_customer: this.notifyCustomer,
        shopify_domain: this.args.shopifyDomain,
        expiry_date: this.expiryDate,
      };

      let response = yield ajax({
        url: '/ember/inbox/shopify/issue_gift_card_compensation',
        type: 'POST',
        data: JSON.stringify(params),
      });

      this.giftCardId = response.gift_card_id;
      this.giftCardCode = response.gift_card_code;
      this.isSuccessState = true;

      this.intercomEventService.trackAnalyticsEvent({
        action: 'issued_gift_card_compensation',
        object: 'shopify_order',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
        gift_card_value: this.parsedGiftCardValue,
        items_count: selectedLineItemQuantities.length,
        notify_customer: this.notifyCustomer,
        has_expiry: true,
      });
    } catch (error) {
      let errorMessage =
        error?.jqXHR?.responseJSON?.error?.message ||
        error?.message ||
        this.intl.t('inbox.conversation-details-sidebar.shopify.gift-card-modal.errors.generic');
      this.notificationsService.notifyError(errorMessage);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::IssueGiftCardModal': typeof IssueGiftCardModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/issue-gift-card-modal': typeof IssueGiftCardModal;
  }
}
